/**
 * PreqGatedRoute — blocks access for company_user accounts that have not yet
 * submitted a prequalification, or whose prequalification failed.
 *
 * Rules:
 *  - admin / reviewer roles → always pass through (no preq requirement)
 *  - company_user with prequalificationStatus 'passed' or 'submitted' → pass through
 *  - company_user with prequalificationStatus 'none' or 'failed' → redirect to /prequalification
 */
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface Props {
  children: React.ReactNode;
}

export default function PreqGatedRoute({ children }: Props) {
  const { user, isLoading } = useAuth();

  // Still loading — ProtectedRoute higher up handles the spinner
  if (isLoading || !user) return null;

  // Privileged roles bypass the gate entirely
  const isPrivileged = user.roles.some((r) =>
    ['super_admin', 'tenant_admin', 'reviewer'].includes(r),
  );
  if (isPrivileged) return <>{children}</>;

  // Company users need a submitted (or passed) prequalification; failed blocks access
  if (user.prequalificationStatus === 'none' || user.prequalificationStatus === 'failed') {
    return <Navigate to="/prequalification" replace state={{ gated: true }} />;
  }

  return <>{children}</>;
}
